import React from 'react';
import { motion } from 'framer-motion';
import { Cake, PartyPopper, Gift, Sparkles, MapPin, Palette, Music, UtensilsCrossed, Ribbon, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
const birthdayTypes = [{
  icon: PartyPopper,
  title: 'Kids Parties',
  description: 'Fun, colorful celebrations that kids will never forget.'
}, {
  icon: Sparkles,
  title: 'Milestone Birthdays',
  description: 'Special celebrations for 16th, 21st, 30th, 50th and beyond.'
}, {
  icon: Gift,
  title: 'Surprise Parties',
  description: 'We help you plan the perfect surprise for your loved one.'
}, {
  icon: Palette,
  title: 'Themed Celebrations',
  description: 'From superheroes to elegant soirées, we bring themes to life.'
}];
const included = [{
  icon: MapPin,
  title: 'Venue',
  description: 'Finding the perfect space for your celebration.'
}, {
  icon: Palette,
  title: 'Theme Design',
  description: 'Creative concepts that match your personality.'
}, {
  icon: Music,
  title: 'Entertainment',
  description: 'Games, music, and activities for all ages.'
}, {
  icon: UtensilsCrossed,
  title: 'Catering',
  description: 'Delicious food and the perfect birthday cake.'
}, {
  icon: Sparkles,
  title: 'Decorations',
  description: 'Balloons, banners, and beautiful setups.'
}, {
  icon: Ribbon,
  title: 'Party Favors',
  description: 'Memorable gifts for your guests to take home.'
}];
export function BirthdaysPage() {
  return <div className="min-h-screen" data-id="element-127">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-amber-50 via-white to-amber-100 pt-24" data-id="element-128">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-129">
          <div className="absolute -top-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-amber-200/40 blur-3xl" data-id="element-130" />
          <div className="absolute bottom-[10%] -left-[10%] w-[400px] h-[400px] rounded-full bg-amber-100/50 blur-3xl" data-id="element-131" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center py-16" data-id="element-132">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-133">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-134">
              <Cake className="w-10 h-10 text-amber-500" data-id="element-135" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-stone-900 mb-6" data-id="element-136">
              Birthday Celebrations
            </h1>
            <p className="text-xl md:text-2xl text-stone-600 max-w-2xl mx-auto" data-id="element-137">
              Creating magical birthday moments for all ages
            </p>
          </motion.div>
        </div>
      </section>

      {/* Types of Birthdays */}
      <section className="py-20 bg-white" data-id="element-138">
        <div className="container mx-auto px-4" data-id="element-139">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-140">
            <h2 className="text-4xl font-bold text-stone-900 mb-4" data-id="element-141">
              Types of Celebrations
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto rounded-full" data-id="element-142" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-143">
            {birthdayTypes.map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-stone-50 rounded-2xl p-8 border border-stone-100 flex gap-6" data-id="element-144">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-145">
                  <item.icon className="w-7 h-7 text-amber-500" data-id="element-146" />
                </div>
                <div data-id="element-147">
                  <h3 className="text-xl font-bold text-stone-900 mb-2" data-id="element-148">
                    {item.title}
                  </h3>
                  <p className="text-stone-600" data-id="element-149">{item.description}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-stone-50" data-id="element-150">
        <div className="container mx-auto px-4" data-id="element-151">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-152">
            <h2 className="text-4xl font-bold text-stone-900 mb-4" data-id="element-153">
              What's Included
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto rounded-full" data-id="element-154" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-155">
            {included.map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 border border-stone-100 text-center" data-id="element-156">
                <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-157">
                  <item.icon className="w-7 h-7 text-amber-500" data-id="element-158" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-3" data-id="element-159">
                  {item.title}
                </h3>
                <p className="text-stone-600" data-id="element-160">{item.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-amber-500 text-white" data-id="element-161">
        <div className="container mx-auto px-4 text-center" data-id="element-162">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} data-id="element-163">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-164">
              Plan the perfect birthday
            </h2>
            <p className="text-xl text-amber-100 mb-10 max-w-2xl mx-auto" data-id="element-165">
              Whether it's a child's first birthday or a golden jubilee, we'll
              make it unforgettable.
            </p>
            <a href="https://wa.me/260968642042" target="_blank" rel="noopener noreferrer" data-id="element-166">
              <Button size="lg" className="bg-white text-amber-600 hover:bg-amber-50 shadow-lg gap-2" data-id="element-167">
                <MessageCircle className="w-5 h-5" data-id="element-168" />
                Start Planning on WhatsApp
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
}